// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

import {
  createRootRoute,
  createRoute,
  createHashHistory,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from "@tanstack/react-router";

import { CrashScreen } from "./components/ErrorBoundary.js";
import { CheckEmailPage } from "./routes/CheckEmailPage.js";
import { HomePage } from "./routes/HomePage.js";
import { LoginPage } from "./routes/LoginPage.js";
import { NotFoundPage } from "./routes/NotFoundPage.js";
import { ResetPasswordPage } from "./routes/ResetPasswordPage.js";
import { SignupPage } from "./routes/SignupPage.js";
import { VerifyEmailPage } from "./routes/VerifyEmailPage.js";
import { isPhoneAppDriveState } from "./lib/driveHandoff.js";
import { isDesktop } from "./lib/platform.js";
import { hasUser, sessionStore } from "./lib/session.js";

/** Reads an optional string search parameter. */
function optionalString<K extends string>(search: Record<string, unknown>, key: K): { [P in K]?: string } {
  const value = search[key];
  return (typeof value === "string" && value !== "" ? { [key]: value } : {}) as { [P in K]?: string };
}

const signedIn = () => hasUser(sessionStore.getState());

const rootRoute = createRootRoute({
  component: Outlet,
  notFoundComponent: NotFoundPage,
  errorComponent: ({ error }) => {
    console.error("Route error", error);
    return <CrashScreen />;
  },
});

/** Signed-out users go to the sign-in page and come back afterwards. */
const requireSignedIn = ({ location }: { location: { href: string } }) => {
  // The desktop app starts with the folders on this computer when nobody is signed in.
  if (!signedIn() && isDesktop && location.href === "/") throw redirect({ to: "/local" });
  if (!signedIn()) {
    throw redirect({ to: "/login", search: location.href === "/" ? {} : { redirect: location.href } });
  }
};

const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: requireSignedIn,
  component: HomePage,
});

const vaultRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/vault/$vaultId/$",
  validateSearch: (s: Record<string, unknown>) => optionalString(s, "file"),
  beforeLoad: requireSignedIn,
  // The editor and workspace load only when a vault opens.
  component: lazyRouteComponent(() => import("./workspace/ui/WorkspacePage.js"), "WorkspacePage"),
});

// A note someone shared for editing (Pro), opened on its own.
const sharedNoteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/shared/$vaultId/$fileId",
  beforeLoad: requireSignedIn,
  component: lazyRouteComponent(() => import("./routes/SharedNotePage.js"), "SharedNotePage"),
});

/** Signed-out pages send signed-in users home. */
const guestOnly = () => {
  if (signedIn()) throw redirect({ to: "/" });
};

// Desktop: folders on this computer. No account needed.
const localVaultsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/local",
  component: lazyRouteComponent(() => import("./routes/LocalVaultsPage.js"), "LocalVaultsPage"),
});

const localVaultRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/local/$localId/$",
  validateSearch: (s: Record<string, unknown>) => optionalString(s, "file"),
  component: lazyRouteComponent(() => import("./workspace/ui/WorkspacePage.js"), "LocalWorkspacePage"),
});

// The desktop app's settings (App, local AI tools, appearance) work without an account.
const settingsAccess = (ctx: { location: { href: string } }) => {
  if (!isDesktop) requireSignedIn(ctx);
};

// Settings sections are paths (/settings/billing). Links from before, /settings?section=billing,
// still arrive from sent emails, Stripe, and older apps: they move to the path.
const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  validateSearch: (s: Record<string, unknown>) => optionalString(s, "section"),
  beforeLoad: (ctx) => {
    if (ctx.search.section) throw redirect({ to: "/settings/$section", params: { section: ctx.search.section }, replace: true });
    settingsAccess(ctx);
  },
  component: lazyRouteComponent(() => import("./routes/SettingsPage.js"), "SettingsPage"),
});

const settingsSectionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/$section",
  beforeLoad: settingsAccess,
  component: lazyRouteComponent(() => import("./routes/SettingsPage.js"), "SettingsPage"),
});

// An app (MCP client) asks for access; the API's /oauth/authorize sends the browser here.
const oauthParams = ["client_id", "redirect_uri", "response_type", "scope", "state", "code_challenge", "code_challenge_method", "resource"] as const;
const oauthConsentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/oauth/authorize",
  validateSearch: (s: Record<string, unknown>) =>
    Object.assign({}, ...oauthParams.map((k) => optionalString(s, k))) as { [K in (typeof oauthParams)[number]]?: string },
  beforeLoad: requireSignedIn,
  component: lazyRouteComponent(() => import("./routes/OAuthConsentPage.js"), "OAuthConsentPage"),
});

// Granite for Shopify: the app in Shopify admin opens this in a new tab with a one-time code
// (ShopifyApp.md 4.1). Signed out, the person signs in and comes back with the code.
const shopifyConnectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/connect/shopify",
  validateSearch: (s: Record<string, unknown>) => optionalString(s, "code"),
  beforeLoad: requireSignedIn,
  component: lazyRouteComponent(() => import("./routes/ShopifyConnectPage.js"), "ShopifyConnectPage"),
});

// The staff admin console. The API decides who is staff; others see a refusal.
// Tabs are paths too (/admin/users); /admin?tab=users moves there.
const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
  validateSearch: (s: Record<string, unknown>) => optionalString(s, "tab"),
  beforeLoad: (ctx) => {
    if (ctx.search.tab && ctx.search.tab !== "overview") throw redirect({ to: "/admin/$tab", params: { tab: ctx.search.tab }, replace: true });
    requireSignedIn(ctx);
  },
  component: lazyRouteComponent(() => import("./routes/AdminPage.js"), "AdminPage"),
});

const adminTabRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/$tab",
  beforeLoad: requireSignedIn,
  component: lazyRouteComponent(() => import("./routes/AdminPage.js"), "AdminPage"),
});

const adminUserRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/users/$userId",
  beforeLoad: requireSignedIn,
  component: lazyRouteComponent(() => import("./routes/AdminDetailPages.js"), "AdminUserPage"),
});

const adminSupportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/support/$messageId",
  beforeLoad: requireSignedIn,
  component: lazyRouteComponent(() => import("./routes/AdminSupport.js"), "AdminSupportPage"),
});

const adminChatRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/chats/$session",
  beforeLoad: requireSignedIn,
  component: lazyRouteComponent(() => import("./routes/AdminAssistant.js"), "AdminAssistantChatPage"),
});

const adminWorkspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/workspaces/$workspaceId",
  beforeLoad: requireSignedIn,
  component: lazyRouteComponent(() => import("./routes/AdminDetailPages.js"), "AdminWorkspacePage"),
});

const workspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/w/$workspaceId",
  beforeLoad: requireSignedIn,
  component: lazyRouteComponent(() => import("./routes/WorkspacePage.js"), "WorkspacePage"),
});

// A workspace's sections are paths too (/w/<id>/members), like Settings. Its billing is on
// Settings' Plan and billing page, with the personal plan (links in billing emails still work).
const workspaceSectionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/w/$workspaceId/$section",
  beforeLoad: (ctx) => {
    requireSignedIn(ctx);
    if (ctx.params.section === "billing") {
      throw redirect({ to: "/settings/$section", params: { section: "billing" }, hash: `workspace-${ctx.params.workspaceId}`, replace: true });
    }
  },
  component: lazyRouteComponent(() => import("./routes/WorkspacePage.js"), "WorkspacePage"),
});

// Invitation links work signed out too: the page offers to sign in and comes back.
const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invite",
  validateSearch: (s: Record<string, unknown>) => optionalString(s, "token"),
  component: lazyRouteComponent(() => import("./routes/InvitePage.js"), "InvitePage"),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (s: Record<string, unknown>) => optionalString(s, "redirect"),
  beforeLoad: ({ search }) => {
    if (signedIn()) throw redirect({ href: search.redirect?.startsWith("/") ? search.redirect : "/" });
  },
  component: LoginPage,
});

const ssoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sso",
  validateSearch: (s: Record<string, unknown>) => ({ ...optionalString(s, "email"), ...optionalString(s, "redirect") }),
  beforeLoad: guestOnly,
  component: lazyRouteComponent(() => import("./routes/SSOPage.js"), "SSOPage"),
});

// Google's sign-in page returns here with an ID token in the fragment (P1-T02; the redirect
// URI https://app.granite.md/auth/google is registered on the Granite Web client).
const googleCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/google",
  component: lazyRouteComponent(() => import("./routes/GoogleCallbackPage.js"), "GoogleCallbackPage"),
});

// Apple posts its answer to the API (https://api.granite.md/v1/auth/oidc/apple/return, the
// return URL on the md.granite.web Services ID), which sends the person on to here with the
// ID token in the fragment (P1-T02).
const appleCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/apple",
  component: lazyRouteComponent(() => import("./routes/AppleCallbackPage.js"), "AppleCallbackPage"),
});

// The desktop app signs in with Google, Apple or single sign-on here, in the browser (P4-T08).
const appSignInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app-signin",
  validateSearch: (s: Record<string, unknown>) => ({
    ...optionalString(s, "challenge"),
    ...optionalString(s, "state"),
    ...optionalString(s, "via"),
    ...optionalString(s, "email"),
  }),
  component: lazyRouteComponent(() => import("./routes/AppSignInPage.js"), "AppSignInPage"),
});

// The identity provider returns here (register https://app.granite.md/sso/callback).
const ssoCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sso/callback",
  validateSearch: (s: Record<string, unknown>) => ({
    ...optionalString(s, "code"),
    ...optionalString(s, "state"),
    ...optionalString(s, "handoff"),
    ...optionalString(s, "error"),
    ...optionalString(s, "error_description"),
  }),
  component: lazyRouteComponent(() => import("./routes/SSOPage.js"), "SSOCallbackPage"),
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  beforeLoad: guestOnly,
  // ?plan=business (from the site's Business page) opens the business account form.
  validateSearch: (s: Record<string, unknown>) => optionalString(s, "plan"),
  component: SignupPage,
});

const checkEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/check-email",
  validateSearch: (s: Record<string, unknown>) => optionalString(s, "email"),
  component: CheckEmailPage,
});

// Email links work whether or not this browser is signed in.
const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/verify-email",
  validateSearch: (s: Record<string, unknown>) => optionalString(s, "token"),
  component: VerifyEmailPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  validateSearch: (s: Record<string, unknown>) => optionalString(s, "token"),
  component: ResetPasswordPage,
});

// Google sends the person back here after connecting Drive (P11-T01).
const driveCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/drive/callback",
  validateSearch: (s: Record<string, unknown>) => ({
    ...optionalString(s, "code"),
    ...optionalString(s, "state"),
    ...optionalString(s, "error"),
  }),
  // A sign-in started in the phone app goes back to the app, signed in on the web or not.
  beforeLoad: (ctx) => {
    if (!isPhoneAppDriveState(ctx.search.state)) requireSignedIn(ctx);
  },
  component: lazyRouteComponent(() => import("./routes/DriveCallbackPage.js"), "DriveCallbackPage"),
});

const routeTree = rootRoute.addChildren([
  homeRoute,
  vaultRoute,
  sharedNoteRoute,
  localVaultsRoute,
  localVaultRoute,
  settingsRoute,
  settingsSectionRoute,
  oauthConsentRoute,
  shopifyConnectRoute,
  workspaceRoute,
  workspaceSectionRoute,
  adminRoute,
  adminTabRoute,
  adminUserRoute,
  adminWorkspaceRoute,
  adminSupportRoute,
  adminChatRoute,
  inviteRoute,
  loginRoute,
  ssoRoute,
  ssoCallbackRoute,
  appSignInRoute,
  googleCallbackRoute,
  appleCallbackRoute,
  driveCallbackRoute,
  signupRoute,
  checkEmailRoute,
  verifyEmailRoute,
  resetPasswordRoute,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: false,
  scrollRestoration: true,
  // The desktop app serves files from disk, where only the root path exists: use #/ paths.
  ...(isDesktop ? { history: createHashHistory() } : {}),
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
